// --- NLLB-200 Distilled 600M Offline Neural Translation Service ---
// Multilingual neural translator (~190 languages) using ONNX Runtime Web via Transformers.js
// 100% offline local browser execution from CacheStorage (nllb-onnx-models-v1)

import { pipeline, env } from '@xenova/transformers';

const R2_NLLB_BASE = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-translator-models/NLLB/';
export const NLLB_MODEL_NAME = 'Xenova/nllb-200-distilled-600M';
export const CACHE_NAME = 'nllb-onnx-models-v1';

// Install fetch interceptor to map local/relative model fetches to CacheStorage entries
if (typeof window !== 'undefined' && !(window as any).__nllb_fetch_interceptor_installed) {
  (window as any).__nllb_fetch_interceptor_installed = true;
  const originalFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const rawUrl = typeof input === 'string' ? input : (input instanceof Request ? input.url : String(input));

    if (rawUrl.includes('nllb') || rawUrl.includes('config') || rawUrl.includes('tokenizer') || rawUrl.includes('.onnx') || rawUrl.includes('generation_config')) {
      try {
        if ('caches' in window) {
          const cache = await caches.open(CACHE_NAME);
          let match = await cache.match(rawUrl);

          if (!match) {
            // Extract filename/path under NLLB
            const m = rawUrl.match(/(Xenova\/nllb-200-distilled-600M\/.*)$/i) || rawUrl.match(/(nllb-200-distilled-600M\/.*)$/i);
            if (m && m[1]) {
              const subPath = m[1].replace(/^Xenova\/nllb-200-distilled-600M\//i, '').replace(/^nllb-200-distilled-600M\//i, '');
              const r2Url = `${R2_NLLB_BASE}${subPath}`;
              match = await cache.match(r2Url);
              if (!match) {
                const tfCache = await caches.open('transformers-cache');
                match = await tfCache.match(r2Url);
              }
            }
          }

          if (match) {
            return match.clone();
          }
        }
      } catch (e) {
        console.warn(`[NLLB Interceptor] Cache check warning for "${rawUrl}":`, e);
      }
    }

    return originalFetch.call(this, input, init);
  };
}

// Configure Transformers.js for 100% local/offline execution
env.allowRemoteModels = false;
env.allowLocalModels = true;
env.localURL = R2_NLLB_BASE;
env.useBrowserCache = true;

if (env.backends?.onnx?.wasm) {
  env.backends.onnx.wasm.numThreads = 1;
}

const REQUIRED_FILES = [
  'config.json',
  'generation_config.json',
  'tokenizer.json',
  'tokenizer_config.json',
  'onnx/encoder_model_quantized.onnx',
  'onnx/decoder_model_merged_quantized.onnx'
];

const DICTIONARY_PAIRS = [
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/en/eng-por/eng-por.json',
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/en/eng-spa/eng-spa.json',
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/en/eng-fra/eng-fra.json',
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/fr/fra-por/fra-por.json',
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/de/deu-por/deu-por.json',
  'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-dict/it/ita-por/ita-por.json'
];

export const NLLB_CODES: Record<string, string> = {
  pt: 'por_Latn',
  en: 'eng_Latn',
  fr: 'fra_Latn',
  es: 'spa_Latn',
  it: 'ita_Latn',
  de: 'deu_Latn',
  ru: 'rus_Cyrl',
  hu: 'hun_Latn',
  ja: 'jpn_Jpan',
  zh: 'zho_Hans',
  ko: 'kor_Hang',
  ar: 'ara_Arab',
  hi: 'hin_Deva',
  nl: 'nld_Latn',
  pl: 'pol_Latn',
  uk: 'ukr_Cyrl',
  tr: 'tur_Latn',
};

export const getNllbCode = (langCode: string): string => {
  if (!langCode) return 'eng_Latn';
  const clean = langCode.toLowerCase().split('-')[0].split('_')[0];
  if (NLLB_CODES[clean]) return NLLB_CODES[clean];
  if (langCode.includes('_')) return langCode;
  return 'eng_Latn';
};

let activeTranslatorPipeline: any = null;

const fetchAndCacheFile = async (url: string, cache: Cache): Promise<void> => {
  const existing = await cache.match(url);
  if (existing) return;

  let retries = 3;
  while (retries > 0) {
    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit'
      });

      if (response.ok) {
        if (response.type === 'opaque') {
          throw new Error(`Resposta opaca recebida para ${url}`);
        }
        await cache.put(url, response.clone());
        try {
          const tfCache = await caches.open('transformers-cache');
          await tfCache.put(url, response.clone());
        } catch (_) {}
        return;
      }
    } catch (e) {
      console.warn(`[NLLBModelService] Fetch retry (${retries - 1} left) for ${url}:`, e);
    }
    retries--;
    if (retries > 0) {
      await new Promise(r => setTimeout(r, 1000));
    }
  }
  throw new Error(`Falha ao baixar arquivo do modelo NLLB: ${url}`);
};

export const checkNllbModelDownloaded = async (): Promise<boolean> => {
  if (typeof caches === 'undefined') return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    for (const file of REQUIRED_FILES) {
      const url = `${R2_NLLB_BASE}${file}`;
      const match = await cache.match(url);
      if (!match) return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

export const downloadNllbModel = async (
  onProgress: (percent: number) => void
): Promise<void> => {
  if (typeof caches === 'undefined') {
    throw new Error('Cache Storage não suportado neste navegador.');
  }

  const cache = await caches.open(CACHE_NAME);
  const modelUrls = REQUIRED_FILES.map(file => `${R2_NLLB_BASE}${file}`);
  const allUrls = [...modelUrls, ...DICTIONARY_PAIRS];

  const checkedStatus = await Promise.all(allUrls.map(async (url) => {
    const match = await cache.match(url);
    return { url, cached: !!match };
  }));

  let completed = checkedStatus.filter(x => x.cached).length;
  onProgress(Math.round((completed / allUrls.length) * 100));

  const toDownload = checkedStatus.filter(x => !x.cached).map(x => x.url);

  if (toDownload.length === 0) {
    onProgress(100);
    return;
  }

  const CONCURRENCY = 2;
  const downloadWorker = async () => {
    while (toDownload.length > 0) {
      const url = toDownload.shift();
      if (url) {
        await fetchAndCacheFile(url, cache);
        completed++;
        onProgress(Math.round((completed / allUrls.length) * 100));
      }
    }
  };

  const workers = Array(Math.min(CONCURRENCY, toDownload.length)).fill(null).map(downloadWorker);
  await Promise.all(workers);
};

export const deleteNllbModel = async (): Promise<void> => {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(CACHE_NAME);
    const tfCache = await caches.open('transformers-cache');
    const tfKeys = await tfCache.keys();
    for (const req of tfKeys) {
      if (req.url.includes('nllb') || req.url.includes('NLLB')) {
        await tfCache.delete(req);
      }
    }
    activeTranslatorPipeline = null;
  } catch (e) {
    console.error('[NLLBModelService] Error deleting model cache:', e);
  }
};

export const loadNllbPipeline = async (): Promise<any> => {
  if (activeTranslatorPipeline) {
    return activeTranslatorPipeline;
  }

  console.log(`[NLLBModelService] Loading NLLB-200 Distilled 600M pipeline from local cache...`);

  const translator = await pipeline('translation', NLLB_MODEL_NAME, {
    quantized: true,
    local_files_only: true,
  });

  activeTranslatorPipeline = translator;
  return translator;
};

const TRANSLATION_CACHE_KEY_PREFIX = 'nllb_trans_v1_';

interface CachedNllbTranslation {
  key: string;
  translation: string;
  timestamp: number;
}

const getCachedNllbTranslation = (cacheKey: string): string | null => {
  try {
    const raw = localStorage.getItem(TRANSLATION_CACHE_KEY_PREFIX + cacheKey);
    if (raw) {
      const parsed: CachedNllbTranslation = JSON.parse(raw);
      return parsed.translation;
    }
  } catch (e) {}
  return null;
};

const setCachedNllbTranslation = (cacheKey: string, translation: string) => {
  try {
    const item: CachedNllbTranslation = {
      key: cacheKey,
      translation,
      timestamp: Date.now()
    };
    localStorage.setItem(TRANSLATION_CACHE_KEY_PREFIX + cacheKey, JSON.stringify(item));
  } catch (e) {
    cleanNllbTranslationCache();
    try {
      localStorage.setItem(TRANSLATION_CACHE_KEY_PREFIX + cacheKey, JSON.stringify({
        key: cacheKey,
        translation,
        timestamp: Date.now()
      }));
    } catch (_) {}
  }
};

const cleanNllbTranslationCache = () => {
  try {
    const items: { key: string; timestamp: number }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(TRANSLATION_CACHE_KEY_PREFIX)) {
        try {
          const val = JSON.parse(localStorage.getItem(key) || '{}');
          items.push({ key, timestamp: val.timestamp || 0 });
        } catch (e) {
          items.push({ key, timestamp: 0 });
        }
      }
    }
    items.sort((a, b) => a.timestamp - b.timestamp);
    const removeCount = Math.max(1, Math.ceil(items.length * 0.25));
    for (let i = 0; i < removeCount; i++) {
      if (items[i]) localStorage.removeItem(items[i].key);
    }
  } catch (e) {}
};

export const nllbModelService = {
  checkAllModelsDownloaded: checkNllbModelDownloaded,
  downloadAllModels: downloadNllbModel,
  deleteModel: deleteNllbModel,

  async translateOffline(
    text: string,
    targetLanguage: string,
    sourceLanguage: string = 'en'
  ): Promise<{ translation: string; source: string } | null> {
    const cleanText = (text || '').trim();
    if (!cleanText) return null;

    const srcCode = getNllbCode(sourceLanguage);
    const tgtCode = getNllbCode(targetLanguage);

    if (srcCode === tgtCode) {
      return {
        translation: cleanText,
        source: 'offline-nllb'
      };
    }

    const cacheKey = `${srcCode}:${tgtCode}:${cleanText.toLowerCase()}`;
    const cachedTranslation = getCachedNllbTranslation(cacheKey);
    if (cachedTranslation) {
      console.log(`[NLLBModelService] Persistent translation cache HIT for "${cleanText}" (${srcCode}->${tgtCode})`);
      return {
        translation: cachedTranslation,
        source: 'offline-nllb'
      };
    }

    try {
      const translator = await loadNllbPipeline();
      const output = await translator(cleanText, {
        src_lang: srcCode,
        tgt_lang: tgtCode,
      });

      let resText = '';
      if (output && Array.isArray(output) && output[0]?.translation_text) {
        resText = output[0].translation_text.trim();
      } else if (typeof output === 'string') {
        resText = (output as string).trim();
      }

      if (resText) {
        setCachedNllbTranslation(cacheKey, resText);
        return {
          translation: resText,
          source: 'offline-nllb'
        };
      }
    } catch (err: any) {
      console.error(`[NLLBModelService] Translation error (${srcCode} -> ${tgtCode}):`, err);
      throw err;
    }

    return null;
  }
};
